import type { FileNameConflictResultDto } from '@/entities/project-file';
import { i18n } from '@/shared/i18n';
import type { IntakeItem, ResolvedUpload } from '../model/types';

/**
 * "Upload as new version" is only possible when there's an uploaded version to
 * stack onto AND the dropped file's type matches the existing file's
 */
export function canUploadAsVersion(item: IntakeItem): boolean {
  return (
    item.existing != null &&
    item.existing.latestVersionId != null &&
    item.existing.type === item.file.type
  );
}

/**
 * Validation message for the rename field, or null when valid. Only applies to a
 * conflicted item in rename mode. Used by the row to render the inline message.
 */
export function renameError(item: IntakeItem): string | null {
  if (!item.existing || item.option !== 'rename') return null;
  const name = item.newName.trim();
  if (name.length === 0) {
    return i18n.t('projects:projectFileUploadGuard.resolution.enterAFileName');
  }
  if (name === item.existing.name) {
    return i18n.t('projects:projectFileUploadGuard.resolution.thisNameIsAlreadyTaken');
  }
  return null;
}

/**
 * Whether an item is ready to upload — the boolean gate for the confirm button.
 * No conflict is always ready; a conflict needs an available version or a valid
 * rename
 */
export function isResolved(item: IntakeItem): boolean {
  if (!item.existing) return true;
  if (item.option === 'version') return canUploadAsVersion(item);
  const name = item.newName.trim();
  return name.length > 0 && name !== item.existing.name;
}

/**
 * Turn the dropped files + the server conflict check into intake items. Each
 * item defaults to the rename option carrying its original name
 */
export function buildItems(files: File[], results: FileNameConflictResultDto[]): IntakeItem[] {
  const conflictByName = new Map(
    results
      .filter((r) => r.status === 'conflicted' && r.conflictsWith)
      .map((r) => [r.name, r.conflictsWith!] as const),
  );
  return files.map((file, index) => ({
    id: String(index),
    file,
    existing: conflictByName.get(file.name) ?? null,
    option: 'rename',
    newName: file.name,
  }));
}

/**
 * Map resolved intake items onto the upload contract the consumer fans out:
 * clean/renamed files become `new` (renames get a renamed File), version
 * choices become `version` targeting the existing file.
 */
export function toResolved(items: IntakeItem[]): ResolvedUpload[] {
  return items.map((item) => {
    if (!item.existing) return { kind: 'new', file: item.file };
    if (item.option === 'version') {
      return {
        kind: 'version',
        file: item.file,
        fileId: item.existing.id,
        knownLatestVersionId: item.existing.latestVersionId ?? null,
      };
    }
    return {
      kind: 'new',
      file: new File([item.file], item.newName.trim(), { type: item.file.type }),
    };
  });
}
